import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Plus, RefreshCw, Settings2, Check, X } from "lucide-react";

export default function OpsCleaningRotations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rotations, setRotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState("");
  const [branches, setBranches] = useState([]);

  const isAdmin = user?.role === "corporate_admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (branchFilter) params.set("branch_id", branchFilter);
      const res = await api.get(`/ops/cleaning/rotations?${params}`);
      setRotations(res.data);
    } catch {
      setRotations([]);
    } finally {
      setLoading(false);
    }
  }, [branchFilter]);

  useEffect(() => {
    api.get("/branches").then(r => setBranches(r.data.filter(b => !b.is_corporate))).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rotaciones de limpieza</h1>
          <p className="text-sm text-slate-500">Plantillas de tareas por horario</p>
        </div>
        {isAdmin && (
          <button
            data-testid="new-rotation-btn"
            onClick={() => navigate("/ops/admin/cleaning-rotations/nueva")}
            className="flex items-center gap-2 px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400"
          >
            <Plus className="w-4 h-4" />
            Nueva rotacion
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={branchFilter}
          onChange={e => setBranchFilter(e.target.value)}
          data-testid="rotation-branch-filter"
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
        >
          <option value="">Todas las sucursales</option>
          <option value="all">Aplicable a todas</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <button
          data-testid="refresh-rotations-btn"
          onClick={load}
          className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50"
        >
          <RefreshCw className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-300" />
        </div>
      ) : rotations.length === 0 ? (
        <div data-testid="empty-rotations" className="text-center py-16 text-slate-400">
          <Settings2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No hay rotaciones configuradas.</p>
          {isAdmin && (
            <button
              onClick={() => navigate("/ops/admin/cleaning-rotations/nueva")}
              className="mt-3 px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg text-sm font-semibold hover:bg-yellow-400"
            >
              Crear primera rotacion
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="rotations-list">
          {rotations.map(r => (
            <div
              key={r.id}
              data-testid={`rotation-card-${r.id}`}
              onClick={() => navigate(`/ops/admin/cleaning-rotations/${r.id}`)}
              className="bg-white border border-slate-200 rounded-xl p-5 cursor-pointer hover:border-slate-300 hover:shadow-sm transition-all space-y-3"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-slate-900 leading-tight">{r.name}</h3>
                <span className={`flex-shrink-0 flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                  r.active
                    ? "bg-green-100 text-green-700"
                    : "bg-slate-100 text-slate-500"
                }`}>
                  {r.active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                  {r.active ? "Activa" : "Inactiva"}
                </span>
              </div>

              {r.description && (
                <p className="text-sm text-slate-500 line-clamp-2">{r.description}</p>
              )}

              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>
                  {r.branch_id === "all" ? "Todas las sucursales" : "Sucursal especifica"}
                </span>
                <span className="font-medium text-slate-600">
                  {(r.tasks || []).length} tarea{(r.tasks || []).length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
